import {match} from 'ts-pattern';

import {groupBy, isNotNil, toPairs} from 'ramda';

import i18n from '@dms/i18n';
import {getLocaleLabel} from '@dms/shared';

import {Nullish} from 'shared/web';

import type {TemplateOption} from '../hooks/useDocumentSelections';

const OTHERS_LOCALE_KEY = '__others__';

export type TemplateOptionGroup = {
  label: string;
  options: TemplateOption[];
};

export function groupTemplateOptionsByLocale(
  options: TemplateOption[],
  localeByTemplateId: Record<string, string | Nullish>,
  preferredLocale: string | Nullish
): TemplateOption[] | TemplateOptionGroup[] {
  const hasAnyLocale = options.some((option) => isNotNil(localeByTemplateId[option.value]));

  if (!hasAnyLocale) {
    return options;
  }

  const grouped = groupBy<TemplateOption>(
    (option) => localeByTemplateId[option.value] ?? OTHERS_LOCALE_KEY,
    options
  );

  return toPairs(grouped)
    .map(([key, optionsInGroup]) => {
      const baseLabel =
        key === OTHERS_LOCALE_KEY ? i18n.t('general.labels.others') : (getLocaleLabel(key) ?? key);
      const label =
        isNotNil(preferredLocale) && key === preferredLocale
          ? `${baseLabel} (${i18n.t('general.labels.preferred')})`
          : baseLabel;

      return {key, label, options: optionsInGroup ?? []};
    })
    .sort((a, b) =>
      match({
        aPreferred: a.key === preferredLocale,
        bPreferred: b.key === preferredLocale,
        aOthers: a.key === OTHERS_LOCALE_KEY,
        bOthers: b.key === OTHERS_LOCALE_KEY,
      })
        .with({aPreferred: true, bPreferred: false}, () => -1)
        .with({aPreferred: false, bPreferred: true}, () => 1)
        .with({aOthers: true, bOthers: false}, () => 1)
        .with({aOthers: false, bOthers: true}, () => -1)
        .otherwise(() => a.label.localeCompare(b.label))
    )
    .map(({label, options: optionsInGroup}) => ({label, options: optionsInGroup}));
}
